/**
 * PortfolioDistributionCard — MIF operational insight only.
 *
 * Displays the weekly-refreshed portfolio-volume distribution as a six-row
 * horizontal bar chart. Every admin role can see the *counts*; only Owner
 * ever receives the raw USD numbers (and those are stripped at the API layer
 * before they reach this component, so there's nothing to gate here).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { PieChart, Zap } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUCKET_LABEL = {
  no_portfolio: 'No portfolio',
  under_100k: 'Under $100k',
  '100k_500k': '$100k-$500k',
  '500k_1m': '$500k-$1M',
  '1m_5m': '$1M-$5M',
  over_5m: 'Over $5M',
};

const BUCKET_COLOR = {
  no_portfolio: 'bg-gray-500',
  under_100k: 'bg-sky-500',
  '100k_500k': 'bg-teal-500',
  '500k_1m': 'bg-emerald-500',
  '1m_5m': 'bg-amber-500',
  over_5m: 'bg-rose-500',
};

const formatRefreshedAt = (iso) => {
  if (!iso) return 'never';
  try {
    return new Date(iso).toLocaleString('en-US', {
      dateStyle: 'medium', timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

export function PortfolioDistributionCard({ onRecalculated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_URL}/api/admin/portfolio-distribution`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`Failed (${resp.status})`);
      setData(await resp.json());
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  const rebuild = useCallback(async () => {
    if (!window.confirm('Recalculate every user\'s portfolio band now?\n\nThis looks up each user\'s current portfolio value, converts it to US Dollars, and puts them in the right band. Usually takes 10-30 seconds. It normally runs automatically every Sunday.')) return;
    setRebuilding(true);
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_URL}/api/admin/portfolio-distribution/refresh`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`Failed (${resp.status})`);
      const summary = await resp.json();
      toast.success(`Recalculated ${summary.touched} users. Updating the chart and the users list…`);
      await load();
      // Notify the parent (AdminUsersTab) so the users table below re-fetches
      // with the freshly-stamped bucket values — otherwise a filter that was
      // already applied (e.g. $1M-$5M) would keep showing stale results.
      if (typeof onRecalculated === 'function') {
        try { onRecalculated(); } catch { /* ignore parent errors */ }
      }
    } catch (e) {
      toast.error(e.message || 'Recalculation failed');
    } finally {
      setRebuilding(false);
    }
  }, [load, onRecalculated]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mb-4 rounded-lg border border-gray-700 bg-gray-900/40 p-4" data-testid="portfolio-distribution-card">
      <Header refreshedAt={data?.last_refreshed_at} loading={loading} onRebuild={rebuild} rebuilding={rebuilding} />
      {error && (
        <p className="text-xs text-rose-400 mt-2" data-testid="portfolio-distribution-error">{error}</p>
      )}
      {!error && (
        <DistributionRows counts={data?.counts} order={data?.bucket_order} total={data?.total} loading={loading} />
      )}
    </div>
  );
}

function Header({ refreshedAt, loading, onRebuild, rebuilding }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-2 text-white">
        <PieChart className="w-4 h-4 text-emerald-400" />
        <h4 className="text-sm font-semibold">Portfolio Distribution</h4>
        <span className="text-[10px] text-gray-500">MIF insight · refreshed weekly (Sun 04:00 UTC)</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[10px] text-gray-500" data-testid="portfolio-distribution-refreshed-at">
          Last refresh: {formatRefreshedAt(refreshedAt)}
        </span>
        {onRebuild && (
          <button
            onClick={onRebuild} disabled={rebuilding || loading}
            className="text-xs px-2 py-1 rounded bg-emerald-600/80 hover:bg-emerald-600 text-white inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="portfolio-distribution-rebuild-btn"
            title="Re-check every user's current portfolio value in US Dollars and re-assign them to the right band. Normally runs automatically every Sunday 04:00 UTC."
          >
            <Zap className={`w-3 h-3 ${rebuilding ? 'animate-pulse' : ''}`} />
            {rebuilding ? 'Recalculating…' : 'Recalculate now'}
          </button>
        )}
      </div>
    </div>
  );
}

function DistributionRows({ counts, order, total, loading }) {
  if (loading && !counts) {
    return <p className="text-xs text-gray-500" data-testid="portfolio-distribution-loading">Loading…</p>;
  }
  if (!counts || !order) return null;
  const maxCount = Math.max(1, ...order.map((k) => counts[k] || 0));
  return (
    <div className="space-y-2" data-testid="portfolio-distribution-rows">
      {order.map((key) => {
        const count = counts[key] || 0;
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        const barPct = Math.max(2, Math.round((count / maxCount) * 100));
        return (
          <div key={key} className="grid grid-cols-[130px_1fr_100px] items-center gap-3" data-testid={`portfolio-distribution-row-${key}`}>
            <span className="text-xs text-gray-300">{BUCKET_LABEL[key] || key}</span>
            <div className="h-3 rounded bg-gray-800 overflow-hidden">
              <div className={`h-full ${BUCKET_COLOR[key] || 'bg-gray-500'} transition-all`} style={{ width: `${barPct}%` }} />
            </div>
            <span className="text-xs text-gray-400 tabular-nums text-right">
              <span className="text-white font-medium" data-testid={`portfolio-distribution-count-${key}`}>{count.toLocaleString()}</span>
              <span className="text-gray-500 ml-1">({pct}%)</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
